// @ts-nocheck
import React, { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import Dropdown from "react-bootstrap/Dropdown"
import Modal from "react-bootstrap/Modal"
import Alert from "react-bootstrap/Alert"
import Spinner from "react-bootstrap/Spinner"
import ScrollToTopButton from "../common/ScrollToTopButton.jsx"
import Toast from "../common/Toast.jsx"
import V2PageContainer from "../common/V2PageContainer.jsx"
import CustomerDetailsSlideout from "./CustomerDetailsSlideout.jsx"
import CustomersDesktopTable from "./CustomersDesktopTable.jsx"
import CustomersFilters from "./CustomersFilters.jsx"
import CustomersHeader from "./CustomersHeader.jsx"
import CustomersMobileList from "./CustomersMobileList.jsx"
import ImportCustomersModal from "./ImportCustomersModal.jsx"
import CreateEditCustomerModal from "./CreateEditCustomerModal.jsx"
import useInfiniteApiList from "../hooks/useInfiniteApiList.js"
import useIsMobileViewport from "../hooks/useIsMobileViewport.js"
import useNotImplementedToast from "../hooks/useNotImplementedToast.js"
import { FIT_PER_PAGE_STORAGE_KEY, readCachedFitPerPage, writeCachedFitPerPage } from "../utils/fitPerPageStorage.js"
import { refetchRemainingMs } from "../utils/loading.js"

const MOBILE_PAGE_SIZE = 25

function mockCustomerFields(customer) {
    const seed = Number(customer.id ?? 0)
    const statuses = ["active", "active", "active", "inactive"]
    const typeSets = [
        ["Sport"],
        ["Wholesale", "Co-pack"],
        ["Sport", "Retail"],
        ["Retail"],
        ["Sport", "Retail", "Co-pack"],
    ]
    const lodges = [
        "Nat King Lodge",
        "West Coast Fishing Club",
        "Rivers Inlet Sportsman's Club",
        "Painter's Lodge",
        "Langara Fishing Adventures",
    ]
    const tripCodes = ["TRIP-124", "TRIP-318", "TRIP-442", "TRIP-559", "TRIP-673"]
    const fishTags = ["FT-0921", "FT-1077", "FT-1145", "FT-1288", "FT-1399"]

    const status = statuses[seed % statuses.length]
    const customerTypes = typeSets[seed % typeSets.length]

    return {
        status,
        customer_types: customerTypes,
        lodge_info: {
            lodge: lodges[seed % lodges.length],
            trip_code: tripCodes[seed % tripCodes.length],
            fish_tag: fishTags[seed % fishTags.length],
        },
    }
}

export default function CustomersPage({
    app,
    api,
    helpers,
    enableStatusColumn = true,
    enableTypeColumn = true,
    showStatusDropdown = true,
    showTypesDropdown = true,
    disableCustomerWritesWhenOffline = false,
    customerSlideoutProps = undefined,
    showFishTagPadding = true,
}) {
    const { toastProps, showNotImplementedToast, showSuccessToast } = useNotImplementedToast()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const [customers, setCustomers] = useState([])
    const [totalRows, setTotalRows] = useState(0)
    const [isLoadingInitialDesktop, setIsLoadingInitialDesktop] = useState(true)
    const [showDesktopRefetchLoader, setShowDesktopRefetchLoader] = useState(false)
    const [searchTerm, setSearchTerm] = useState(searchParams.get("search") ?? "")
    const [fitPerPage, setFitPerPage] = useState(
        () => readCachedFitPerPage(FIT_PER_PAGE_STORAGE_KEY.customers) ?? 25
    )
    const isMobile = useIsMobileViewport()
    const [selectedCustomer, setSelectedCustomer] = useState(
        /** @type {any | null} */(null)
    )
    const [isSlideoutOpen, setIsSlideoutOpen] = useState(false)
    const SLIDEOUT_ANIM_MS = 300
    const [showImportModal, setShowImportModal] = useState(false)
    const [refetchToken, setRefetchToken] = useState(0)
    const [showCustomerModal, setShowCustomerModal] = useState(false)
    const [customerModalMode, setCustomerModalMode] = useState(
        /** @type {"create" | "edit"} */ ("create")
    )
    const [customerModalCustomer, setCustomerModalCustomer] = useState(
        /** @type {any | null} */ (null)
    )
    const pageParam = searchParams.get("page")
    const perPageParam = searchParams.get("per_page")
    const searchParam = searchParams.get("search")
    const sortByParam = searchParams.get("sort_by")
    const sortDirParam = searchParams.get("sort_dir")

    const [selectedCustomerIds, setSelectedCustomerIds] = useState(
        /** @type {Array<string|number>} */([])
    )
    const [showDeleteCustomersModal, setShowDeleteCustomersModal] = useState(false)
    const [deleteCustomersLoading, setDeleteCustomersLoading] = useState(false)
    const [deleteCustomersError, setDeleteCustomersError] = useState("")
    const [deleteCustomersProgress, setDeleteCustomersProgress] = useState(
        /** @type {{ completed: number, total: number } | null} */(null)
    )

    const customerWritesDisabledBecauseOffline = Boolean(disableCustomerWritesWhenOffline && app?.connected === false)
    const customerWritesDisabledTooltip = "Cannot perform this action while offline."

    const desktopRequestIdRef = React.useRef(0)
    const hasLoadedDesktopOnceRef = React.useRef(false)
    const desktopFetchInFlightRef = React.useRef(0)

    function getPage() {
        const parsed = Number(pageParam)
        return Number.isInteger(parsed) && parsed > 0 ? parsed : 1
    }

    function getPerPage() {
        if (!perPageParam || (perPageParam ?? "").toLowerCase() === "fit") {
            return fitPerPage
        }
        const parsed = Number(perPageParam)
        return Number.isInteger(parsed) && parsed > 0 ? parsed : 25
    }

    function isPerPageFit() {
        return !perPageParam || (perPageParam ?? "").toLowerCase() === "fit"
    }

    function getSearch() {
        return searchParam ?? ""
    }

    function getSortBy() {
        return sortByParam ?? "created_at"
    }

    function getSortDirection() {
        return sortDirParam === "asc" ? "asc" : "desc"
    }

    function getSortDropdownValue() {
        const sortBy = getSortBy()
        const dir = getSortDirection()
        if (sortBy === "created_at" && dir === "desc") return "created-desc"
        if (sortBy === "created_at" && dir === "asc") return "created-asc"
        if (sortBy === "first_name" && dir === "desc") return "name-desc"
        return "name-asc"
    }

    function handleSortDropdownChange(nextValue) {
        const nextParams = new URLSearchParams(searchParams)
        if (nextValue === "created-desc") {
            nextParams.set("sort_by", "created_at")
            nextParams.set("sort_dir", "desc")
        } else if (nextValue === "created-asc") {
            nextParams.set("sort_by", "created_at")
            nextParams.set("sort_dir", "asc")
        } else if (nextValue === "name-desc") {
            nextParams.set("sort_by", "first_name")
            nextParams.set("sort_dir", "desc")
        } else {
            nextParams.set("sort_by", "first_name")
            nextParams.set("sort_dir", "asc")
        }
        nextParams.set("page", "1")
        setSearchParams(nextParams)
    }

    function handlePageChange(nextPage) {
        const nextParams = new URLSearchParams(searchParams)
        nextParams.set("page", String(nextPage))
        setSearchParams(nextParams)
    }

    function handlePerPageChange(nextPerPage) {
        const nextParams = new URLSearchParams(searchParams)
        if (nextPerPage === "fit") {
            nextParams.set("per_page", "fit")
        } else {
            nextParams.set("per_page", String(nextPerPage))
        }
        nextParams.set("page", "1")
        setSearchParams(nextParams)
    }

    function handleFitPerPageChange(nextFitPerPage) {
        setFitPerPage((current) => {
            if (!Number.isInteger(nextFitPerPage) || nextFitPerPage <= 0) {
                return current
            }
            if (current === nextFitPerPage) {
                return current
            }
            writeCachedFitPerPage(FIT_PER_PAGE_STORAGE_KEY.customers, nextFitPerPage)
            return nextFitPerPage
        })
    }

    function handleSearchSubmit(event) {
        event.preventDefault()
        const nextParams = new URLSearchParams(searchParams)
        if (!searchTerm.trim()) {
            nextParams.delete("search")
        } else {
            nextParams.set("search", searchTerm.trim())
        }
        nextParams.set("page", "1")
        setSearchParams(nextParams)
    }

    function onRefreshClick() {
        setRefetchToken((current) => current + 1)
    }

    function closeDeleteCustomersModal() {
        if (deleteCustomersLoading) {
            return
        }
        setShowDeleteCustomersModal(false)
        setDeleteCustomersError("")
        setDeleteCustomersProgress(null)
    }

    async function confirmDeleteSelectedCustomers() {
        const ids = (selectedCustomerIds ?? []).filter((id) => id != null)
        if (ids.length === 0) {
            return
        }
        if (!api?.deleteCustomer) {
            setDeleteCustomersError("Delete API is unavailable.")
            return
        }

        setDeleteCustomersLoading(true)
        setDeleteCustomersError("")
        setDeleteCustomersProgress({ completed: 0, total: ids.length })

        const failures = []
        for (let i = 0; i < ids.length; i += 1) {
            const id = ids[i]
            try {
                // eslint-disable-next-line no-await-in-loop
                await api.deleteCustomer(id)
            } catch (e) {
                failures.push({ id, error: e })
            } finally {
                setDeleteCustomersProgress({ completed: i + 1, total: ids.length })
            }
        }

        setDeleteCustomersLoading(false)

        if (failures.length > 0) {
            const firstMessage = (
                failures[0]?.error?.message ?
                    String(failures[0].error.message) :
                    "One or more customers failed to delete."
            )
            setDeleteCustomersError(
                failures.length === 1 ?
                    `Failed to delete customer ${failures[0].id}. ${firstMessage}` :
                    `Failed to delete ${failures.length} customers. First error: ${firstMessage}`
            )
        } else {
            setShowDeleteCustomersModal(false)
            setSelectedCustomerIds([])
            setDeleteCustomersError("")
            setDeleteCustomersProgress(null)
        }

        // Background refresh after bulk operations (e.g. delete).
        setRefetchToken((current) => current + 1)
    }

    function mapCustomerForList(customer) {
        return {
            ...customer,
            ...mockCustomerFields(customer),
        }
    }

    useEffect(() => {
        setSearchTerm(searchParam ?? "")
    }, [searchParam])

    const mobileCustomersList = useInfiniteApiList({
        enabled: isMobile,
        perPage: MOBILE_PAGE_SIZE,
        deps: [searchParam, sortByParam, sortDirParam, refetchToken, api],
        fetchPage: async (page, perPage) => {
            const body = await api.getCustomers(
                page,
                perPage,
                getSortBy(),
                getSortDirection(),
                getSearch()
            )
            return {
                items: (body?.data ?? []).map((customer) => mapCustomerForList(customer)),
                total: body?.total ?? 0,
            }
        },
    })

    useEffect(() => {
        if (isMobile) {
            return
        }
        const isRefetch = hasLoadedDesktopOnceRef.current
        const fetchStartedAt = Date.now()

        if (!isRefetch) {
            setIsLoadingInitialDesktop(true)
        }
        if (isRefetch) {
            desktopFetchInFlightRef.current += 1
            setShowDesktopRefetchLoader(true)
        }
        const requestId = ++desktopRequestIdRef.current

        function completeDesktopRefetchSlot() {
            desktopFetchInFlightRef.current = Math.max(0, desktopFetchInFlightRef.current - 1)
            if (desktopFetchInFlightRef.current === 0) {
                setShowDesktopRefetchLoader(false)
            }
        }

        api.getCustomers(
            getPage(),
            getPerPage(),
            getSortBy(),
            getSortDirection(),
            getSearch()
        )
            .then((body) => {
                if (requestId !== desktopRequestIdRef.current) {
                    if (isRefetch) {
                        completeDesktopRefetchSlot()
                    }
                    return
                }
                const mapped = (body?.data ?? []).map((customer) => (
                    mapCustomerForList(customer)
                ))
                const total = body?.total ?? 0
                if (!isRefetch) {
                    setCustomers(mapped)
                    setTotalRows(total)
                    return
                }
                const delay = refetchRemainingMs(fetchStartedAt)
                window.setTimeout(() => {
                    if (requestId !== desktopRequestIdRef.current) {
                        completeDesktopRefetchSlot()
                        return
                    }
                    setCustomers(mapped)
                    setTotalRows(total)
                    completeDesktopRefetchSlot()
                }, delay)
            })
            .catch((e) => {
                if (requestId !== desktopRequestIdRef.current) {
                    if (isRefetch) {
                        completeDesktopRefetchSlot()
                    }
                    return
                }
                console.error("Failed to load v2 customers:", e)
                if (!isRefetch) {
                    return
                }
                const delay = refetchRemainingMs(fetchStartedAt)
                window.setTimeout(() => {
                    if (requestId !== desktopRequestIdRef.current) {
                        completeDesktopRefetchSlot()
                        return
                    }
                    completeDesktopRefetchSlot()
                }, delay)
            })
            .finally(() => {
                if (requestId !== desktopRequestIdRef.current) {
                    return
                }
                if (!hasLoadedDesktopOnceRef.current) {
                    hasLoadedDesktopOnceRef.current = true
                    setIsLoadingInitialDesktop(false)
                }
            })
    }, [isMobile, pageParam, perPageParam, fitPerPage, searchParam, sortByParam, sortDirParam, refetchToken, api])

    function openCustomerSlideout(nextCustomer) {
        const nextId = nextCustomer?.id ?? null
        const currentId = selectedCustomer?.id ?? null

        if (isSlideoutOpen) {
            if (nextId != null && nextId === currentId) {
                setIsSlideoutOpen(false)
                return
            }

            setIsSlideoutOpen(false)
            window.setTimeout(() => {
                setSelectedCustomer(nextCustomer)
                window.setTimeout(() => setIsSlideoutOpen(true), 0)
            }, SLIDEOUT_ANIM_MS)
            return
        }

        setSelectedCustomer(nextCustomer)
        window.setTimeout(() => setIsSlideoutOpen(true), 0)
    }

    function handleAddOrderClick(customer) {
        navigate("/v2/order-form", {
            state: { prefillCustomer: customer ?? null },
        })
    }

    return (
        <V2PageContainer>
            <Toast {...toastProps} />
            <CustomersHeader
                actionsSlot={
                    <Dropdown>
                        <Dropdown.Toggle
                            variant="outline-secondary"
                            size="sm"
                            className="shadow-sm"
                        >
                            Actions{selectedCustomerIds?.length ? ` (${selectedCustomerIds.length})` : ""}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {customerWritesDisabledBecauseOffline ? (
                                <span className="d-block" title={customerWritesDisabledTooltip}>
                                    <Dropdown.Item disabled>
                                        Delete ({selectedCustomerIds?.length ?? 0})
                                    </Dropdown.Item>
                                </span>
                            ) : (
                                <Dropdown.Item
                                    disabled={(selectedCustomerIds?.length ?? 0) === 0}
                                    onClick={() => {
                                        setShowDeleteCustomersModal(true)
                                        setDeleteCustomersError("")
                                        setDeleteCustomersProgress(null)
                                    }}
                                >
                                    Delete ({selectedCustomerIds?.length ?? 0})
                                </Dropdown.Item>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                }
                onNewCustomerClick={() => {
                    setCustomerModalMode("create")
                    setCustomerModalCustomer(null)
                    setShowCustomerModal(true)
                }}
                onImportCustomersClick={() => setShowImportModal(true)}
                disableImportCustomers={customerWritesDisabledBecauseOffline}
                disableNewCustomer={customerWritesDisabledBecauseOffline}
                disabledTooltip={customerWritesDisabledTooltip}
            />
            <Modal show={showDeleteCustomersModal} onHide={closeDeleteCustomersModal} centered>
                <Modal.Header closeButton={!deleteCustomersLoading}>
                    <Modal.Title>Delete Customers</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-2">
                        You are about to permanently delete {selectedCustomerIds.length} customer{selectedCustomerIds.length === 1 ? "" : "s"}.
                    </p>
                    <p className="text-danger mb-3" style={{ fontSize: "0.875rem" }}>
                        This action is permanent and cannot be undone.
                    </p>

                    {deleteCustomersError ? (
                        <Alert variant="danger" className="mb-3">
                            {deleteCustomersError}
                        </Alert>
                    ) : null}

                    {deleteCustomersLoading ? (
                        <div className="d-flex align-items-center gap-2 text-secondary">
                            <Spinner animation="border" size="sm" />
                            <span>
                                Deleting…{" "}
                                {deleteCustomersProgress ? `${deleteCustomersProgress.completed}/${deleteCustomersProgress.total}` : ""}
                            </span>
                        </div>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={closeDeleteCustomersModal}
                        disabled={deleteCustomersLoading}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-danger"
                        onClick={confirmDeleteSelectedCustomers}
                        disabled={deleteCustomersLoading || selectedCustomerIds.length === 0}
                    >
                        {deleteCustomersLoading ? "Deleting…" : `Delete (${selectedCustomerIds.length})`}
                    </button>
                </Modal.Footer>
            </Modal>
            <CustomersFilters
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
                onSearchSubmit={handleSearchSubmit}
                onRefresh={onRefreshClick}
                onNotImplemented={() => showNotImplementedToast()}
                showStatusDropdown={showStatusDropdown}
                showTypesDropdown={showTypesDropdown}
                sortValue={getSortDropdownValue()}
                onSortChange={handleSortDropdownChange}
            />
            {!isMobile ? (
                <div className="flex-grow-1 d-flex flex-column">
                    <CustomersDesktopTable
                        customers={customers}
                        isLoadingInitial={isLoadingInitialDesktop}
                        totalRows={totalRows}
                        page={getPage()}
                        perPage={getPerPage()}
                        onPageChange={handlePageChange}
                        onPerPageChange={handlePerPageChange}
                        perPageSelection={isPerPageFit() ? "fit" : getPerPage()}
                        onFitPerPageChange={handleFitPerPageChange}
                        selectedCustomerIds={selectedCustomerIds}
                        onSelectedCustomerIdsChange={setSelectedCustomerIds}
                        onCustomerClick={(customer) => {
                            openCustomerSlideout(customer)
                        }}
                        onAddOrderClick={handleAddOrderClick}
                        showRefetchLoader={showDesktopRefetchLoader}
                        enableStatusColumn={enableStatusColumn}
                        enableTypeColumn={enableTypeColumn}
                    />
                </div>
            ) : (
                <>
                    <div className="mb-3">
                        <p className="text-secondary mb-0" style={{ fontSize: "0.875rem" }}>
                            Showing {mobileCustomersList.total} customers
                        </p>
                    </div>
                    <CustomersMobileList
                        customers={mobileCustomersList.items}
                        hasMore={mobileCustomersList.hasMore}
                        isLoading={mobileCustomersList.isLoading}
                        isLoadingInitial={mobileCustomersList.isLoadingInitial}
                        onLoadMore={mobileCustomersList.loadMore}
                        onCustomerClick={(customer) => {
                            openCustomerSlideout(customer)
                        }}
                        onAddOrderClick={handleAddOrderClick}
                        enableStatusColumn={enableStatusColumn}
                        enableTypeColumn={enableTypeColumn}
                        selectedCustomerIds={selectedCustomerIds}
                        onSelectedCustomerIdsChange={setSelectedCustomerIds}
                    />
                </>
            )}
            <ScrollToTopButton className="d-lg-none" />
            <CustomerDetailsSlideout
                api={api}
                helpers={helpers}
                customer={selectedCustomer}
                isOpen={isSlideoutOpen}
                onClose={() => setIsSlideoutOpen(false)}
                onCreateOrderClick={() => handleAddOrderClick(selectedCustomer)}
                onEditCustomerClick={() => {
                    if (!selectedCustomer) {
                        return
                    }
                    setCustomerModalMode("edit")
                    setCustomerModalCustomer(selectedCustomer)
                    setShowCustomerModal(true)
                }}
                disableEditCustomer={customerWritesDisabledBecauseOffline}
                editCustomerDisabledTooltip="Cannot edit customers while offline."
                onCommunicationClick={() => showNotImplementedToast()}
                onSeeAllRecentOrdersClick={() => showNotImplementedToast()}
                {...(customerSlideoutProps ?? {})}
            />
            <ImportCustomersModal
                show={showImportModal}
                onClose={() => setShowImportModal(false)}
                api={api}
                app={app}
                showFishTagPadding={showFishTagPadding}
                onSuccess={showSuccessToast}
                onImported={() => {
                    setRefetchToken((current) => current + 1)
                }}
            />
            <CreateEditCustomerModal
                show={showCustomerModal}
                mode={customerModalMode}
                customer={customerModalCustomer}
                api={api}
                onClose={() => setShowCustomerModal(false)}
                onSaved={(saved) => {
                    setRefetchToken((current) => current + 1)
                    // After save, prefer refetching the canonical customer shape (incl. nested addresses)
                    // instead of trusting the write response payload.
                    const id = saved?.id ?? null
                    if (!id || typeof api?.getCustomer !== "function") {
                        return
                    }
                    api.getCustomer(id)
                        .then((full) => {
                            const resolved = full?.data ?? full
                            if (resolved?.id) {
                                setSelectedCustomer(resolved)
                            }
                        })
                        .catch(() => {
                            // Non-blocking: the list refetch will still refresh visible data.
                        })
                }}
            />
        </V2PageContainer>
    )
}
